/**
 * The app-level dialogs, mounted once by the app shell.
 *
 * In the prototype these lived at the bottom of the 4,800-line component, which is
 * why every screen that could raise one had to be handed a setter. They are raised
 * from uiStore now, so a route only has to say `setDeleteTarget(song)` and the
 * dialog appears — no prop threading, and no chance of a screen forgetting to
 * render the dialog it triggers.
 *
 * The dialogs themselves are pure presentation (see ./index.ts). This file is the
 * one place that connects them to the stores and hooks, so the decisions stay out
 * of the presentational components.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddToPlaylistModal from './AddToPlaylistModal';
import ChangePasswordModal from './ChangePasswordModal';
import DeleteAccountModal from './DeleteAccountModal';
import DeletePlaylistModal from './DeletePlaylistModal';
import DeleteReleaseModal from './DeleteReleaseModal';
import DeleteSongModal from './DeleteSongModal';
import EditSongModal from './EditSongModal';
import LyricsModal from './LyricsModal';
import MakeDiscoverableModal from './MakeDiscoverableModal';
import OutOfCreditsModal from './OutOfCreditsModal';
import ProfilePictureModal from './ProfilePictureModal';
import RemoveFromCatalogueModal from './RemoveFromCatalogueModal';
import RenamePlaylistModal from './RenamePlaylistModal';
import { authApi, GOOGLE_SIGN_IN_CHANGE_PASSWORD_MESSAGE } from '../../api/authApi';
import { apiErrorMessage } from '../../api/errors';
import { attributionFor, attributionForUser } from '../../lib/attribution';
import { usePlayer, usePlaylists, usePublisher, useShareSong, useSongs } from '../../hooks';
import { paths } from '../../routes/paths';
import { selectAvatarUrl, useAuthStore } from '../../stores/authStore';
import { useCreditsStore } from '../../stores/creditsStore';
import { useUiStore } from '../../stores/uiStore';
import type { Song } from '../../types/domain';

export interface AppModalsProps {
  onOpenFullscreen: (song: Song) => void;
}

export default function AppModals({ onOpenFullscreen }: AppModalsProps) {
  /*
    The one navigation this file does, added with Batch 6 §9's "Open in Studio" on the
    Library's song detail view. Every other dialog here ends in a store write, which is why
    this is the only router hook in the file — and it is the same `paths.song` route the
    row's More menu uses rather than a second way in. AppModals is rendered inside AppShell,
    which is inside the router, so the hook is always in context.
  */
  const navigate = useNavigate();
  // One field per selector: Zustand v5 compares with Object.is, so an object
  // literal here would return a new reference on every unrelated store change.
  const deleteTarget = useUiStore((s) => s.deleteTarget);
  const deleteSurface = useUiStore((s) => s.deleteSurface);
  const publisherDeleteTarget = useUiStore((s) => s.publisherDeleteTarget);
  const publisherDeleteSurface = useUiStore((s) => s.publisherDeleteSurface);
  const editTarget = useUiStore((s) => s.editTarget);
  const outOfCreditsOpen = useUiStore((s) => s.outOfCreditsOpen);
  const deleteAccountOpen = useUiStore((s) => s.deleteAccountOpen);
  const changePasswordOpen = useUiStore((s) => s.changePasswordOpen);
  const profilePictureOpen = useUiStore((s) => s.profilePictureOpen);
  const lyricsTarget = useUiStore((s) => s.lyricsTarget);
  const lyricsSurface = useUiStore((s) => s.lyricsSurface);
  const addToPlaylistTarget = useUiStore((s) => s.addToPlaylistTarget);
  const makeDiscoverableTarget = useUiStore((s) => s.makeDiscoverableTarget);
  const removeFromCatalogueTarget = useUiStore((s) => s.removeFromCatalogueTarget);
  const renamePlaylistTarget = useUiStore((s) => s.renamePlaylistTarget);
  const deletePlaylistTarget = useUiStore((s) => s.deletePlaylistTarget);
  const setDeleteTarget = useUiStore((s) => s.setDeleteTarget);
  const setPublisherDeleteTarget = useUiStore((s) => s.setPublisherDeleteTarget);
  const setEditTarget = useUiStore((s) => s.setEditTarget);
  const setOutOfCreditsOpen = useUiStore((s) => s.setOutOfCreditsOpen);
  const setDeleteAccountOpen = useUiStore((s) => s.setDeleteAccountOpen);
  const setChangePasswordOpen = useUiStore((s) => s.setChangePasswordOpen);
  const setProfilePictureOpen = useUiStore((s) => s.setProfilePictureOpen);
  const setLyricsTarget = useUiStore((s) => s.setLyricsTarget);
  const setAddToPlaylistTarget = useUiStore((s) => s.setAddToPlaylistTarget);
  const setMakeDiscoverableTarget = useUiStore((s) => s.setMakeDiscoverableTarget);
  const setRemoveFromCatalogueTarget = useUiStore((s) => s.setRemoveFromCatalogueTarget);
  const setRenamePlaylistTarget = useUiStore((s) => s.setRenamePlaylistTarget);
  const setDeletePlaylistTarget = useUiStore((s) => s.setDeletePlaylistTarget);
  const showToast = useUiStore((s) => s.showToast);
  const setSelectedPack = useCreditsStore((s) => s.setSelectedPack);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // The profile picture goes through the account's own store and API — the same
  // `updateProfile` path the name and email use — rather than a separate image
  // service, so there is one place a profile is written.
  const avatarUrl = useAuthStore(selectAvatarUrl);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  /*
    The signed-in account, for the Make Discoverable dialog's attribution preview only.

    `user` and not the `selectProfileName` selector, which falls back to a display name for
    an unauthenticated read: that fallback is right for a header that must render something,
    and wrong here, because it would preview a real person's name as the credit on a song
    listed with no session. Null is the honest value and renders no preview.
  */
  const authUser = useAuthStore((s) => s.user);
  const authProvider = useAuthStore((s) => s.user?.authProvider ?? 'password');
  const clearSession = useAuthStore((s) => s.clearSession);

  const { deleteSong, updateSong, toggleFavorite, makeDiscoverable, removeFromCatalogue } =
    useSongs();
  const { scheduleReleaseDeletion } = usePublisher();
  /*
    The app's one share action, which the catalogue cards now use too — see
    hooks/useShareSong. Its body used to live inline below; two copies of it is how two
    share buttons end up toasting different things, or one of them dropping the caveat that
    the link does not resolve yet.
  */
  const shareSong = useShareSong();
  // The app's one player, so the lyrics dialog's Play is the same playback every other
  // surface uses rather than a second sound system living inside a dialog.
  //
  // `isActive` and the clock are read for §10: the lyric sheet follows the song by
  // section, and the position it follows comes from the same store every other surface
  // reads. See the `at` prop below, and songs/LyricSheet for what "follow" claims.
  const { isActive, isPlaying, toggle, elapsed, durationSec, loading, pendingStart } = usePlayer();
  const {
    playlists,
    playlistsWith,
    summaryFor,
    addSong,
    createPlaylist,
    renamePlaylist,
    deletePlaylist,
  } = usePlaylists();

  return (
    <>
      <DeleteSongModal
        song={deleteTarget}
        surface={deleteSurface}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteSong(deleteTarget);
          setDeleteTarget(null);
        }}
      />

      <DeleteReleaseModal
        release={publisherDeleteTarget}
        onClose={() => setPublisherDeleteTarget(null)}
        onConfirm={() => {
          if (!publisherDeleteTarget) return;
          scheduleReleaseDeletion(publisherDeleteTarget.id);
          setPublisherDeleteTarget(null);
          if (publisherDeleteSurface === 'detail') {
            navigate(paths.publisherDashboard);
          }
        }}
      />

      <EditSongModal
        song={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={({ title, genre }) => {
          if (!editTarget) return;
          // An emptied title falls back to the existing one rather than saving a
          // blank — the prototype's `editTitle.trim() || s.title`.
          updateSong(editTarget.id, { title: title.trim() || editTarget.title, genre });
          showToast('Song updated');
          setEditTarget(null);
        }}
      />

      <OutOfCreditsModal
        open={outOfCreditsOpen}
        onClose={() => setOutOfCreditsOpen(false)}
        onBuy={(packId) => {
          // The modal no longer grants credits locally. It hands the chosen pack off to the
          // real top-up screen, which opens external JVZoo checkout from there.
          setSelectedPack(packId);
          setOutOfCreditsOpen(false);
          navigate(paths.topup);
        }}
      />

      <ChangePasswordModal
        open={changePasswordOpen}
        error={changePasswordError}
        pending={changingPassword}
        onClose={() => {
          setChangePasswordError(null);
          setChangePasswordOpen(false);
        }}
        onConfirm={({ currentPassword, newPassword }) => {
          if (authProvider !== 'password') {
            setChangePasswordError(GOOGLE_SIGN_IN_CHANGE_PASSWORD_MESSAGE);
            return;
          }
          setChangingPassword(true);
          setChangePasswordError(null);
          void authApi
            .changePassword({ currentPassword, newPassword })
            .then(() => {
              showToast('Password changed');
              setChangePasswordOpen(false);
            })
            .catch((err: unknown) => {
              setChangePasswordError(apiErrorMessage(err, { preserveMessage: true }));
            })
            .finally(() => setChangingPassword(false));
        }}
      />

      <DeleteAccountModal
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        onConfirm={({ reason, detail }) => {
          /*
            CLOSED BEFORE THE REQUEST STARTS, matching the other confirm dialogs: the
            user has already confirmed, and leaving the sheet open behind a later redirect
            would trap focus on a dialog for an account that may no longer exist.

            ON SUCCESS the session is cleared locally without calling logout again. The
            delete route already ended the backend session by deleting the account and
            clearing the cookie, so a second POST /auth/logout after that would be the
            wrong transport shape.
          */
          setDeleteAccountOpen(false);
          void authApi
            .requestAccountDeletion(reason, detail)
            .then(() => {
              clearSession();
              showToast('Account deleted');
              navigate(paths.login, { replace: true });
            })
            .catch((err: unknown) => showToast(apiErrorMessage(err, { preserveMessage: false })));
        }}
      />

      {/* ---- the profile picture, raised by clicking the avatar ----
          Saves through authStore.updateProfile, which is the same action the name and
          email would use: the picture is a field on the account, not a system of its
          own. NOTHING IS UPLOADED — the store keeps the data URL the browser read, and
          the dialog tells the user so on screen. See api/authApi.updateProfile. */}
      <ProfilePictureModal
        open={profilePictureOpen}
        onClose={() => setProfilePictureOpen(false)}
        currentUrl={avatarUrl}
        onSave={async (nextUrl) => {
          // undefined, not null, when clearing: `updateProfile` spreads the patch over
          // the user record, and null would leave an explicit null on a field whose
          // absence is what "no picture" means.
          await updateProfile({ avatarUrl: nextUrl ?? undefined });
          showToast(nextUrl ? 'Profile picture updated' : 'Profile picture removed');
        }}
      />

      {/* ---- the song detail view, raised by the catalogue AND by the Library ----
          Every surface calls `setLyricsTarget(song)` rather than navigating, which is what
          keeps a visitor discovering music out of the Studio. All the actions are wired to
          things that already exist: the one player, the one favourite flag, the
          add-to-playlist sheet, the share link and the Studio's own route.

          `surface` is the Batch 6 §9 variant. The Library passes 'library' when it raises the
          target and gets Play, Share and Open in Studio; everything else defaults to
          'catalogue' and keeps Play, Like, Save and Share. Both branches are wired here
          unconditionally — the dialog decides which buttons exist, so this file does not have
          to know the rule twice. */}
      <LyricsModal
        song={lyricsTarget}
        surface={lyricsSurface}
        onClose={() => setLyricsTarget(null)}
        playing={Boolean(lyricsTarget && isPlaying(lyricsTarget.id))}
        /*
          §10 — the position the sheet follows, as a share of the track rather than in
          seconds. The sheet's sections are placed by their share of the TEXT (lib/sections),
          so a fraction is the only comparable thing; see songs/LyricSheet.

          `isActive`, not `isPlaying`, matching the timeline's playhead: pausing should leave
          the highlight where the track stopped rather than snapping it back to the first
          verse. Null for any song that is not the loaded one, and `durationSec` belongs to
          that same loaded song — which is why it is safe to divide by here and would not be
          for an arbitrary target.
        */
        at={
          lyricsTarget && isActive(lyricsTarget.id) && durationSec > 0
            ? elapsed / durationSec
            : null
        }
        elapsedSec={lyricsTarget && isActive(lyricsTarget.id) ? elapsed : null}
        loading={Boolean(lyricsTarget && isActive(lyricsTarget.id) && (loading || pendingStart))}
        /*
          Playback is NOT stopped on close and the dialog is NOT closed on play: the
          player is global, so a track started here keeps going in the mini player while
          the visitor carries on browsing. Closing it would make Play look like a
          navigation.
        */
        onTogglePlay={toggle}
        onToggleLike={toggleFavorite}
        onSave={(song) => {
          // Handed over rather than stacked: two dialogs open at once would trap focus in
          // the wrong one. The preview closes and the playlist sheet takes its place.
          setLyricsTarget(null);
          setAddToPlaylistTarget(song);
        }}
        /*
          The same handler the catalogue cards' Share uses, so the two cannot drift — each
          outcome's words, and the caveat that the link does not resolve yet, live in one
          place now. See hooks/useShareSong.
        */
        onShare={shareSong}
        /*
          Closed on the way, unlike Play: this one IS a navigation, and leaving a dialog
          mounted over the screen you just arrived at would trap focus above the workspace.

          Nothing here checks the Studio entitlement — RequireStudio on `/library/:songId`
          owns that decision for every entry point, so an unentitled user lands on the same
          upgrade screen the row's More menu item takes them to rather than on a dead button.
          Only rendered on the Library surface; see LyricsModal.
        */
        onOpenInStudio={(song) => {
          setLyricsTarget(null);
          navigate(paths.song(song.id));
        }}
        onOpenFullscreen={(song) => {
          setLyricsTarget(null);
          onOpenFullscreen(song);
        }}
      />

      <AddToPlaylistModal
        song={addToPlaylistTarget}
        playlists={playlists}
        /*
          Computed here rather than inside the dialog so the dialog stays presentational,
          like the other four. Guarded on the target because playlistsWith needs an id
          and the dialog is mounted whether or not it is open.
        */
        containingIds={
          addToPlaylistTarget ? playlistsWith(addToPlaylistTarget.id).map((p) => p.id) : []
        }
        summaryFor={summaryFor}
        onClose={() => setAddToPlaylistTarget(null)}
        onAdd={(playlist) => {
          if (!addToPlaylistTarget) return;
          addSong(playlist, addToPlaylistTarget);
          // Closed on a duplicate press too: `addSong` toasts "already in", which is
          // the answer, and holding the dialog open after it would imply the press
          // failed rather than that there was nothing to do.
          setAddToPlaylistTarget(null);
        }}
        onCreate={(name) => {
          if (!addToPlaylistTarget) return;
          createPlaylist(name, addToPlaylistTarget.id, addToPlaylistTarget);
          setAddToPlaylistTarget(null);
        }}
      />

      {/* ---- the two listing decisions ----
          Both go through useSongs, which goes through songsStore.setVisibility — the one
          path visibility changes on. Neither dialog touches the song record itself, and
          the READY guard lives in the hook rather than here, so it holds for any caller
          and not only for the dialog a user happened to confirm. */}
      <MakeDiscoverableModal
        song={makeDiscoverableTarget}
        /*
          HOW THE CATALOGUE WILL CREDIT IT, shown before the owner decides to list it.

          THE RECORD'S OWN CREATOR FIRST, and the signed-in account only as an explicit
          second choice — which is the one place in this app where that second choice is
          legitimate. This dialog is only ever reached from the owner's own Library, acting
          on their own song, so "you" and "the creator" are the same person here; that is
          why `attributionForUser` exists as a SEPARATE function rather than as a fallback
          inside `attributionFor`, and why the fallback is written out here where the
          justification can sit beside it. No catalogue surface does this — see
          lib/attribution and songs/DiscoveryCard.

          It matters because of the seed songs: they carry no creator, so `attributionFor`
          alone would return null and the preview would vanish for exactly the songs a
          user in this build is most likely to list. The line they are shown is then the
          line a real backend would produce, since the backend derives the creator from
          the same session.

          Null when nobody is signed in or the account has no name, which renders no
          preview at all rather than a partial one.
        */
        attribution={
          makeDiscoverableTarget
            ? (attributionFor(makeDiscoverableTarget) ?? attributionForUser(authUser))
            : null
        }
        onClose={() => setMakeDiscoverableTarget(null)}
        onConfirm={() => {
          if (!makeDiscoverableTarget) return;
          makeDiscoverable(makeDiscoverableTarget);
          setMakeDiscoverableTarget(null);
        }}
      />

      <RemoveFromCatalogueModal
        song={removeFromCatalogueTarget}
        onClose={() => setRemoveFromCatalogueTarget(null)}
        onConfirm={() => {
          if (!removeFromCatalogueTarget) return;
          removeFromCatalogue(removeFromCatalogueTarget);
          setRemoveFromCatalogueTarget(null);
        }}
      />

      <RenamePlaylistModal
        playlist={renamePlaylistTarget}
        onClose={() => setRenamePlaylistTarget(null)}
        onSave={(name) => {
          if (!renamePlaylistTarget) return;
          // An unchanged or emptied name is refused inside the hook, which also owns
          // the toast — so this closes either way rather than deciding again here.
          renamePlaylist(renamePlaylistTarget, name);
          setRenamePlaylistTarget(null);
        }}
      />

      <DeletePlaylistModal
        playlist={deletePlaylistTarget}
        onClose={() => setDeletePlaylistTarget(null)}
        onConfirm={() => {
          if (!deletePlaylistTarget) return;
          deletePlaylist(deletePlaylistTarget);
          setDeletePlaylistTarget(null);
        }}
      />
    </>
  );
}
